import React, { useMemo } from "react";
import { QueryKey } from "react-query";
import { useTranslation } from "react-i18next";

import {
  BusinessRole,
  Company,
  PermissionLevel,
  UserPermission,
} from "fe-shared/src/types/domain";
import { Banner } from "fe-shared/src/components";
import {
  Corrections,
  Transaction,
  TransactionFlag,
  TransactionSection,
  TransactionStatus,
  TransactionType,
} from "fe-shared-transactions/src/types";
import buildProcessorWarehouse from "fe-shared-transactions/src/helpers/buildProcessorWarehouse";
import {
  CorrectionsDetails,
  DetailsDataSection,
  DetailsLocationsSection,
  DetailsParticipantsSection,
  AdjustmentDetails,
} from "fe-shared-transactions/src/components";

import { PERMISSIONS } from "consts";
import FlagComments from "../FlagComments";
import CorrectionRequestWarning from "../CorrectionRequestWarning";
import { actions } from "./modules/config";

interface Props {
  transaction: Transaction;
  linkedAdjustment?: Transaction;
  corrections: Corrections | null;
  isPermitted: (permission: UserPermission) => boolean;
  company: Company;
  activitiesQueryKey: QueryKey;
  transactionQueryKey: QueryKey;
  correctionsQueryKey: QueryKey;
  beforeCorrections?: React.ReactNode;
  correctionsExtraActions?: React.ReactNode;
}

const { Suspended } = TransactionStatus;

const TransactionContentView = ({
  transaction,
  linkedAdjustment,
  corrections,
  isPermitted,
  company,
  activitiesQueryKey,
  transactionQueryKey,
  correctionsQueryKey,
  beforeCorrections,
  correctionsExtraActions,
}: Props) => {
  const { t } = useTranslation();
  const {
    sourceWarehouse,
    targetWarehouse,
    transactionFlags,
    transactionType,
    statusCode,
    submittedBy,
    targetCompanyInfo,
  } = transaction;

  const isAdjustment = transactionType === TransactionType.Adjustment;
  const isDisposition = transactionType === TransactionType.Disposition;
  const isSuspended = statusCode === Suspended;
  const isCorrectionExists = !!corrections?.lastModifiedBy;
  const isProcessor = company.businessRoleCode === BusinessRole.Processor;
  const isInitiatedDelivery =
    transactionType === TransactionType.Delivery &&
    statusCode === TransactionStatus.Synchronizing;

  const canOpenRequest = isPermitted({
    name: PERMISSIONS.requestDetails,
    level: PermissionLevel.Read,
  });

  const processorWarehouse = isInitiatedDelivery
    ? buildProcessorWarehouse(targetCompanyInfo)
    : undefined;

  const visibleTransactionFlags = useMemo(
    () => transactionFlags.filter((item) => item.isVisibleToParticipant),
    [transactionFlags]
  );

  const flagsBySection = useMemo(
    () =>
      Object.values(TransactionSection).reduce((result, s) => {
        result[s] = visibleTransactionFlags.filter((f) => f.sectionCode === s);
        return result;
      }, {} as { [key in TransactionSection]: TransactionFlag[] }),
    [visibleTransactionFlags]
  );

  const hasFlags = !!visibleTransactionFlags.length;
  const isRequestAlert = hasFlags && isSuspended;

  const getFlagComments = (sectionCode: TransactionSection) => (
    <FlagComments flags={flagsBySection[sectionCode]} />
  );

  return (
    <>
      <DetailsLocationsSection
        transaction={transaction}
        isInitiatedDelivery={isInitiatedDelivery}
        canOpenRequest={canOpenRequest}
      >
        {isRequestAlert &&
          (!isCorrectionExists || corrections?.isSubmitted) && (
            <CorrectionRequestWarning transactionFlags={transactionFlags} />
          )}
        {!isRequestAlert && getFlagComments(TransactionSection.Locations)}
      </DetailsLocationsSection>
      {transaction.isAutoApproved && (
        <Banner id="auto-approval-info" variant="info" title={t("info")}>
          {t("transactions.details.autoApprovalInfo")}
        </Banner>
      )}
      <DetailsParticipantsSection
        sourceWarehouse={sourceWarehouse}
        targetWarehouse={processorWarehouse ?? targetWarehouse}
        transactionType={transactionType}
        submittedBy={submittedBy}
      >
        {!isRequestAlert && getFlagComments(TransactionSection.Participants)}
      </DetailsParticipantsSection>
      <DetailsDataSection
        transactionOrTransfer={transaction}
        displayBufferDetails={isProcessor}
      >
        {!isRequestAlert && getFlagComments(TransactionSection.Data)}
        {beforeCorrections}
        {!isAdjustment && !isDisposition && (
          <CorrectionsDetails
            transaction={transaction}
            corrections={corrections}
            isPermitted={isPermitted}
            actions={actions}
            company={company}
            activitiesQueryKey={activitiesQueryKey}
            transactionQueryKey={transactionQueryKey}
            correctionsQueryKey={correctionsQueryKey}
            extraActions={correctionsExtraActions}
          />
        )}
        {linkedAdjustment &&
          linkedAdjustment.statusCode === TransactionStatus.Approved &&
          false && (
            <AdjustmentDetails
              linkedAdjustment={linkedAdjustment!}
              parentTransactionType={transactionType}
            />
          )}
      </DetailsDataSection>
    </>
  );
};

export default TransactionContentView;
